const SOURCE_LABELS = {
  fold: 'Fold',
  railway_variable: 'Railway variable',
  default: 'Default',
};

const REVERT_TARGET_LABELS = {
  railway_variable: 'Railway value',
  default: 'default',
};

const PICKUP_TEXT = {
  next_call: 'Takes effect right away everywhere in Flock',
  about_15_seconds: 'Takes effect within about 15 seconds',
};

export function formatCount(value) {
  return Number(value).toLocaleString('en-US');
}

export function sourceLabel(source) {
  return SOURCE_LABELS[source] ?? source;
}

export function pickupText(pickup) {
  return PICKUP_TEXT[pickup] ?? null;
}

export function isPinned(limit) {
  return limit.source === 'fold';
}

export function revertLabel(limit) {
  const target = REVERT_TARGET_LABELS[limit.fallbackSource] ?? 'Railway value';
  return `Revert to ${target} (${formatCount(limit.fallbackValue)})`;
}

export function usageSummary(limit) {
  if (limit.usedToday === null || limit.usedToday === undefined) {
    return { text: 'Not aggregated', state: 'not_aggregated' };
  }
  const text = `${formatCount(limit.usedToday)} of ${formatCount(limit.value)} used today`;
  if (limit.value === 0) return { text, state: 'paused' };
  if (limit.usedToday >= limit.value) return { text, state: 'reached' };
  return { text, state: 'ok' };
}

export function formatUsageDate(usageDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(usageDate ?? '');
  if (!match) return usageDate || '—';
  const [, year, month, day] = match;
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).toLocaleDateString(
    'en-US',
    { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }
  );
}

export function rangeMessage(limit) {
  return `Enter a whole number from ${formatCount(limit.foldMin)} through ${formatCount(limit.foldMax)}.`;
}

function parseLimitValue(text, limit) {
  const trimmed = String(text ?? '').trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isSafeInteger(value) || value < limit.foldMin || value > limit.foldMax) return null;
  return value;
}

function impactWarning(limit, next) {
  if (limit.kind === 'daily' && next === 0) {
    return `0 pauses ${limit.label} until you raise it.`;
  }
  // Calls are counted before the cap check, so a cap equal to today's count already blocks the next call.
  if (Number.isInteger(limit.usedToday) && next <= limit.usedToday) {
    return "Today's usage is already at or above this value; this pauses further calls until midnight CT.";
  }
  return null;
}

/**
 * What saving `draft` against the `limit` snapshot would do.
 * `value` is the wire value (null = revert); `next` is the effective value afterwards
 * as the web server sees it. `needsAcknowledgement` mirrors the server rule: a raise,
 * a first pin, or a revert.
 */
export function planChange(limit, draft) {
  if (draft.target === 'revert') {
    return {
      ok: true,
      value: null,
      next: limit.fallbackValue,
      change: 'revert',
      needsAcknowledgement: true,
      warning: impactWarning(limit, limit.fallbackValue),
    };
  }

  const value = parseLimitValue(draft.text, limit);
  if (value === null) return { ok: false, message: rangeMessage(limit) };

  let change;
  if (!isPinned(limit)) change = 'pin';
  else if (value === limit.value) change = 'unchanged';
  else change = value > limit.value ? 'raise' : 'lower';

  return {
    ok: true,
    value,
    next: value,
    change,
    needsAcknowledgement: change === 'pin' || change === 'raise',
    warning: change === 'unchanged' ? null : impactWarning(limit, value),
  };
}

export function buildUpdateBody(draft, plan) {
  return {
    value: plan.value,
    expectedVersion: draft.base.version,
    acknowledgeIncrease: plan.needsAcknowledgement && draft.acknowledged === true,
    reason: draft.reason.trim() || null,
  };
}

/** True when `limit` already holds what a save of wire `value` asked for. */
export function outcomeHolds(limit, value) {
  if (value === null) return !isPinned(limit);
  return isPinned(limit) && limit.value === value;
}

function responseMessage(data) {
  if (typeof data?.message === 'string' && data.message) return data.message;
  if (typeof data?.error === 'string' && data.error) return data.error;
  if (typeof data?.error?.message === 'string' && data.error.message) return data.error.message;
  return null;
}

/**
 * Classify an axios failure from the runtime-limits API.
 * `no_response` means the request may or may not have reached the server.
 */
export function readRequestError(error, fallbackMessage) {
  const response = error?.response;
  if (!response) return { type: 'no_response' };

  const data = response.data ?? {};
  const message = responseMessage(data) || error?.message || fallbackMessage;
  const csrfFailure = data.code === 'CSRF_TOKEN_INVALID';

  if (response.status === 401 || (response.status === 403 && !csrfFailure)) {
    return { type: 'denied', message };
  }
  if (response.status === 409 && data.code === 'RUNTIME_LIMIT_CHANGED' && data.limit) {
    return { type: 'conflict', current: data.limit };
  }
  return { type: 'failed', message };
}
